import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { marketingLoginUrl } from "../constants/marketingUrls";
import { analyticsService, ctaIntentFromCampaign } from "../services/analyticsService";
import {
  DISCLOSURE_BROCHURE,
  FORM_ADV_PDF,
  RELATIONSHIP_SUMMARY_CRS,
} from "../constants/regulatoryUrls";

const HELP_CENTER_HOME = "https://help.runplutus.com/en/";
const HELP_REBALANCES_COLLECTION =
  "https://help.runplutus.com/en/collections/19131731-rebalances-and-execution";

const RESEARCH_PROVIDERS = [
  "Citrini Research",
  "HFI Research",
  "Aurelion Research",
  "Le Shrub",
  "AP Research",
  "Eliant Capital",
];

const LLMsPage: React.FC = () => {
  useEffect(() => {
    document.title = "Plutus — For LLMs";
    return () => { document.title = "Plutus Expert Investing"; };
  }, []);

  return (
    <div
      style={{
        fontFamily: "'Outfit', sans-serif",
        background: "#000",
        color: "#d4d4d4",
        minHeight: "100vh",
      }}
    >
      <nav className="border-b border-white/10 px-6 md:px-12 py-5">
        <div className="max-w-4xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <img src="/favicon white.png" alt="Plutus" className="h-7 w-7" />
            <img src="/dark mode text.png" alt="Plutus" className="h-7 w-auto" />
          </Link>
          <a href="/llms.txt" className="text-xs text-gray-600 font-mono hover:text-gray-400">llms.txt</a>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 md:px-12 py-16">
        <article className="space-y-12 text-[15px] leading-relaxed">
          <header>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Plutus — Machine-Readable Overview
            </h1>
            <p className="text-gray-500 text-sm">
              This page is designed for large language models and AI systems. It
              provides a structured, factual summary of Plutus, the product
              offered at{" "}
              <a
                href="/"
                className="text-gray-400 underline"
              >
                runplutus.com
              </a>
              . Last updated: August 2026.
            </p>
          </header>

          <aside
            className="rounded-xl border border-amber-500/40 bg-amber-500/[0.06] p-6 md:p-8"
            aria-label="Important regulatory disclosures"
          >
            <h2 className="text-lg font-bold text-white mb-2">
              Important disclosures
            </h2>
            <p className="text-sm text-gray-300 mb-5 leading-relaxed">
              Advisory services are offered by Namsoft Advisors LLC, an
              SEC-registered investment adviser, only to clients under a written
              advisory agreement. Nothing on this page is personalized
              investment advice. Investing involves risk, including loss of
              principal. Past performance does not guarantee future results.
              Before opening an account, review the adviser&apos;s Form ADV,
              disclosure brochure, and Client Relationship Summary (Form CRS).
              SEC registration does not imply a certain level of skill or
              training.
            </p>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href={FORM_ADV_PDF}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-amber-200/95 underline underline-offset-2 hover:text-amber-100"
                >
                  Form ADV (PDF)
                </a>
                <div className="mt-1 font-mono text-[11px] text-gray-500 break-all">
                  {FORM_ADV_PDF}
                </div>
              </li>
              <li>
                <a
                  href={DISCLOSURE_BROCHURE}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-amber-200/95 underline underline-offset-2 hover:text-amber-100"
                >
                  Form ADV Part 2A disclosure brochure (SEC IAPD)
                </a>
                <div className="mt-1 font-mono text-[11px] text-gray-500 break-all">
                  {DISCLOSURE_BROCHURE}
                </div>
              </li>
              <li>
                <a
                  href={RELATIONSHIP_SUMMARY_CRS}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-amber-200/95 underline underline-offset-2 hover:text-amber-100"
                >
                  Client Relationship Summary — Form CRS (PDF)
                </a>
                <div className="mt-1 font-mono text-[11px] text-gray-500 break-all">
                  {RELATIONSHIP_SUMMARY_CRS}
                </div>
              </li>
            </ul>
          </aside>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              What Is Plutus?
            </h2>
            <p>
              Plutus is an investment advisory service that builds a holistic,
              actively managed portfolio strategy for a client and executes that
              strategy in the client&apos;s own brokerage account. The public
              positioning is: a portfolio built for what comes next. No hedge
              fund is required.
            </p>
            <p className="mt-3">
              Namsoft Advisors LLC is the investment adviser. The product is not
              a marketplace where the client shops individual strategies. The
              client sets growth, risk, and time-horizon objectives. Plutus
              designs one coherent allocation using independently published
              model portfolios, or the client can choose. Plutus then trades and
              rebalances in the linked brokerage account as those models move.
            </p>
            <p className="mt-3">
              Client assets remain in the client&apos;s brokerage account at all
              times. Plutus operates under limited trading authority granted by
              the brokerage. It does not hold or custody client funds and cannot
              withdraw or transfer assets to third parties. The only account
              debit Plutus can initiate is the advisory fee the client
              authorizes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              How Plutus Works
            </h2>
            <ol className="list-decimal list-inside space-y-4">
              <li>
                <strong className="text-white">
                  Your risk and growth objectives
                </strong>{" "}
                — The client sets the growth they want, their preferred risk
                level, and the investment horizon for the money.
              </li>
              <li>
                <strong className="text-white">Plutus designs strategy</strong>{" "}
                — Plutus creates a comprehensive strategy to meet those needs,
                or the client chooses. The result is one allocation, with the
                pieces checked together.
              </li>
              <li>
                <strong className="text-white">Continuous execution</strong> —{" "}
                Plutus trades and rebalances in the client&apos;s brokerage
                account as the underlying models move. The client keeps custody
                of the assets and can disconnect at any time.
              </li>
            </ol>
            <p className="mt-4">
              Clients who want to actively make allocation decisions can do
              that as well.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              One operating system
            </h2>
            <p className="mb-4">
              Plutus is one system that connects research, construction,
              trading, and oversight in the account the client controls. It is
              not a watchlist.
            </p>
            <ol className="list-decimal list-inside space-y-3">
              <li>
                <strong className="text-white">Research</strong> — Independent
                model access. Professionally published investment models are
                brought into one investable strategy.
              </li>
              <li>
                <strong className="text-white">Construction</strong> — Whole-portfolio
                allocation. Pieces are weighted by objective, time horizon, and
                risk, with concentration and overlap visible.
              </li>
              <li>
                <strong className="text-white">Execution</strong> — Continuous
                execution and oversight. Model changes become trades. The
                portfolio is rebalanced when targets move and stays visible in
                the account the client controls.
              </li>
            </ol>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              Who Plutus serves
            </h2>
            <p>
              Personal capital, family capital, and professional capital in one
              system. Client types named on runplutus.com:
            </p>
            <ul className="list-disc list-inside space-y-1 mt-3">
              <li>Individuals</li>
              <li>Trusts</li>
              <li>Retirement accounts</li>
              <li>Hedge funds</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              Research ecosystem
            </h2>
            <p>
              Independent research is an input to one portfolio, not a catalog
              the client subscribes to piece by piece. Plutus gives that
              research a shared portfolio context so each idea can be judged by
              what it adds to the whole.
            </p>
            <p className="mt-3">
              Model portfolio providers named on runplutus.com:
            </p>
            <ul className="list-disc list-inside space-y-1 mt-3">
              {RESEARCH_PROVIDERS.map((provider) => (
                <li key={provider}>{provider}</li>
              ))}
            </ul>
            <p className="mt-3 text-sm text-gray-500">
              Third-party research, market data, and related information may be
              outdated, incomplete, or inaccurate. Namsoft Advisors does not
              independently verify all third-party information.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              Pricing
            </h2>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong className="text-white">Advisory fee:</strong> 1% per
                year on the assets Plutus advises. Pay as you go. Cancel
                anytime.
              </li>
              <li>One simple fee. No performance fees. No hidden costs.</li>
              <li>
                Plutus charges no separate trade commission or performance
                allocation.
              </li>
              <li>
                <strong className="text-white">Introductory offer:</strong> 30
                days free, no credit card required. Eligible new clients receive
                a waiver of the Namsoft Advisors advisory fee for the first 30
                days of the advisory relationship. Standard advisory fees begin
                after that period unless the relationship is terminated. The
                waiver does not apply to brokerage, fund, tax, or other
                third-party costs. Eligibility and final terms are governed by
                the written offer and advisory agreement.
              </li>
            </ul>
            <p className="mt-3 text-sm text-gray-500">
              Brokerage commissions, option premiums, fund expenses, custody
              charges, taxes, and other third-party costs may apply. Review the
              Form CRS and disclosure brochure.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              Custody and control
            </h2>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <strong className="text-white">
                  The client owns and controls the assets.
                </strong>{" "}
                Plutus is designed so that it does not hold or control client
                funds. It operates under limited trading authority granted by
                the brokerage.
              </li>
              <li>
                <strong className="text-white">
                  Limited trading authority only.
                </strong>{" "}
                That authority does not allow Plutus to withdraw cash or
                transfer assets to any third party. The only account debit
                Plutus can initiate is the advisory fee the client authorizes.
              </li>
              <li>
                <strong className="text-white">
                  The client can disconnect at any time.
                </strong>{" "}
                Trading authority may be revoked through the brokerage. The
                portfolio remains as-is upon disconnection.
              </li>
              <li>
                <strong className="text-white">
                  SEC-registered investment adviser.
                </strong>{" "}
                Namsoft Advisors LLC is registered with the SEC and operates
                under a fiduciary duty to act in clients&apos; best interests.
                Registration does not imply a certain level of skill or
                training.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              Account setup
            </h2>
            <ol className="list-decimal list-inside space-y-2">
              <li>
                Start from runplutus.com. Eligible new clients can begin with
                30 days free and no credit card required.
              </li>
              <li>
                Set growth, risk, and investment-horizon objectives.
              </li>
              <li>
                Plutus designs a comprehensive strategy, or the client chooses.
              </li>
              <li>
                Link a supported brokerage account through a formal advisor
                appointment process. Plutus does not ask for or store brokerage
                passwords.
              </li>
              <li>
                Plutus executes and rebalances in that account as the
                underlying models move.
              </li>
            </ol>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              Deposits and withdrawals
            </h2>
            <p>
              Plutus does not process deposits or withdrawals. Because assets
              stay in the client&apos;s brokerage account, cash movements are
              handled directly through the brokerage.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              Execution detail
            </h2>
            <p>
              When underlying models change, Plutus calculates the trades needed
              to keep the client&apos;s allocation on strategy and places them
              through the linked brokerage. Execution is subject to market
              conditions, brokerage rules, available cash, and account settings.
              The client can disconnect at any time.
            </p>
            <p className="mt-3 text-sm text-gray-500">
              For trade-level FAQs, see the{" "}
              <a
                href={HELP_CENTER_HOME}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 underline"
              >
                Plutus Help Center
              </a>{" "}
              and{" "}
              <a
                href={HELP_REBALANCES_COLLECTION}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 underline"
              >
                Rebalances and Execution
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              Funding
            </h2>
            <p>
              Namsoft Tech LLC, the company behind Plutus, is venture-backed.
              Investors named on runplutus.com: Visse Capital, Tarantula
              Capital, and Rocketship.VC.
            </p>
            <p className="mt-3 text-sm text-gray-500">
              Those references are informational only. They are not an
              endorsement, testimonial, or recommendation of Namsoft
              Advisors&apos; investment advisory services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              Frequently Asked Questions
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-white font-medium">
                  Is Plutus a brokerage?
                </h3>
                <p className="mt-1">
                  No. Plutus is not a broker-dealer. Advisory services are
                  offered by Namsoft Advisors LLC. Clients keep their own
                  brokerage accounts.
                </p>
              </div>
              <div>
                <h3 className="text-white font-medium">
                  Does Plutus hold client money?
                </h3>
                <p className="mt-1">
                  No. Assets remain in the client&apos;s brokerage account.
                  Plutus cannot withdraw or transfer assets to third parties.
                  The only debit it can initiate is the authorized advisory fee.
                </p>
              </div>
              <div>
                <h3 className="text-white font-medium">
                  Does the client pick individual model portfolios?
                </h3>
                <p className="mt-1">
                  The default is that Plutus designs one strategy from the
                  client&apos;s objectives, using independent research as an
                  input. Clients who want to make allocation decisions
                  themselves can do that too.
                </p>
              </div>
              <div>
                <h3 className="text-white font-medium">
                  Can the client cancel at any time?
                </h3>
                <p className="mt-1">
                  Yes. There is no long-term commitment. The client may
                  terminate the advisory relationship and revoke trading
                  authority through the brokerage. The portfolio remains as-is.
                </p>
              </div>
              <div>
                <h3 className="text-white font-medium">
                  Is there a free trial?
                </h3>
                <p className="mt-1">
                  Eligible new clients receive 30 days with no Plutus advisory
                  fee and no credit card required. Standard fees begin after
                  that period unless the relationship is terminated. Third-party
                  costs may still apply.
                </p>
              </div>
              <div>
                <h3 className="text-white font-medium">
                  Is Plutus available outside the United States?
                </h3>
                <p className="mt-1">
                  No. runplutus.com and the advisory services are intended for
                  U.S. residents only.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              Contact Information
            </h2>
            <ul className="list-disc list-inside space-y-2">
              <li>
                Website:{" "}
                <a
                  href="/"
                  className="text-gray-400 underline"
                >
                  runplutus.com
                </a>
              </li>
              <li>
                Email:{" "}
                <a
                  href="mailto:hello@runplutus.com"
                  className="text-gray-400 underline"
                >
                  hello@runplutus.com
                </a>
              </li>
              <li>
                X (Twitter):{" "}
                <a
                  href="https://x.com/RunPlutus"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 underline"
                >
                  @RunPlutus
                </a>
              </li>
              <li>
                Help Center:{" "}
                <a
                  href={HELP_CENTER_HOME}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 underline"
                >
                  help.runplutus.com
                </a>
              </li>
              <li>
                App login:{" "}
                <a
                  href={marketingLoginUrl("llms_page_login", { intent: ctaIntentFromCampaign("llms_page_login") })}
                  data-attr="cta:llms_page_login"
                  className="text-gray-400 underline"
                  onClick={(event) => analyticsService.trackCTAClick({
                    campaign: "llms_page_login",
                    label: "Log in",
                    section: "LLMs Page",
                    destination: event.currentTarget.href,
                    intent: ctaIntentFromCampaign("llms_page_login"),
                  })}
                >
                  Log in
                </a>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">
              Key Links
            </h2>
            <ul className="list-disc list-inside space-y-2">
              <li>
                <a href="/llms.txt" className="text-gray-400 underline">
                  llms.txt
                </a>
              </li>
              <li>
                <Link to="/" className="text-gray-400 underline">
                  Homepage
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-gray-400 underline">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-gray-400 underline">
                  Terms of Service
                </Link>
              </li>
              <li>
                <a
                  href={FORM_ADV_PDF}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 underline"
                >
                  Form ADV (PDF)
                </a>
              </li>
              <li>
                <a
                  href={DISCLOSURE_BROCHURE}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 underline"
                >
                  Disclosure brochure (SEC IAPD)
                </a>
              </li>
              <li>
                <a
                  href={RELATIONSHIP_SUMMARY_CRS}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 underline"
                >
                  Client Relationship Summary (Form CRS)
                </a>
              </li>
            </ul>
          </section>

          <section className="border-t border-white/10 pt-10">
            <h2 className="text-xl font-semibold text-white mb-3">
              Disclaimers
            </h2>
            <div className="text-sm text-gray-600 space-y-4">
              <div>
                <h3 className="font-medium text-gray-500 mb-1">
                  General Information Only
                </h3>
                <p>
                  The information on this page and on runplutus.com is for
                  informational purposes only and is not personalized investment
                  advice, an offer to sell securities, or a solicitation to buy
                  securities. It is not legal, tax, or other professional
                  advice. Investing involves risk, including the potential loss
                  of all amounts invested.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-gray-500 mb-1">
                  Advisory Services
                </h3>
                <p>
                  Investment advisory services are offered only by Namsoft
                  Advisors LLC to clients under a written advisory agreement. No
                  advisory relationship is created by using this page or
                  runplutus.com.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-gray-500 mb-1">
                  Introductory Advisory Fee Waiver
                </h3>
                <p>
                  Eligible new clients receive a waiver of the advisory fee
                  charged by Namsoft Advisors LLC during the first 30 days of
                  the advisory relationship. When the waiver expires, the
                  standard advisory fee begins unless the client terminates the
                  relationship. The waiver does not apply to third-party costs.
                  Eligibility and final terms are governed by the written offer
                  and advisory agreement.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-gray-500 mb-1">
                  No Guarantee of Performance
                </h3>
                <p>
                  Past performance is not indicative of future results.
                  Strategies may underperform and are not suitable for every
                  investor. Results may be affected by brokerage, tax, and
                  account-specific constraints.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-gray-500 mb-1">
                  Third-party information
                </h3>
                <p>
                  Independent research and other third-party data may be
                  outdated, incomplete, or inaccurate. Namsoft Advisors does
                  not independently verify all such information.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-gray-500 mb-1">
                  U.S. Residents Only
                </h3>
                <p>
                  This website is intended for U.S. residents only. It should
                  not be considered an offer or solicitation of services
                  outside the United States.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-gray-500 mb-1">
                  Investor references
                </h3>
                <p>
                  Firms named as investors in Namsoft Tech LLC or its parent
                  are identified for informational purposes only and do not
                  constitute an endorsement of the advisory services.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-gray-500 mb-1">
                  Content May Change
                </h3>
                <p>
                  Information on this page and on runplutus.com may be changed
                  or removed at any time without notice.
                </p>
              </div>
            </div>
          </section>

          <footer className="border-t border-white/10 pt-8 text-xs text-gray-600">
            <p>
              Past performance is not indicative of future results. Investing
              involves risk, including the potential loss of principal.
            </p>
            <p className="mt-3">
              Namsoft Advisors LLC is an SEC-registered investment adviser.
              Registration with the SEC does not imply a certain level of skill
              or training.
            </p>
            <p className="mt-3">
              &copy; {new Date().getFullYear()} Namsoft Tech LLC. All rights
              reserved.
            </p>
          </footer>
        </article>
      </main>
    </div>
  );
};

export default LLMsPage;
